import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BookOpen, Users, Award, DollarSign, TrendingUp, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/stats`, authHeader())
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const STAT_CARDS = stats ? [
    { icon: BookOpen, label: "Total Trainings", value: stats.total_trainings, color: "bg-blue-50 text-blue-700", iconBg: "bg-blue-100" },
    { icon: Users, label: "Total Registrations", value: stats.total_participants, color: "bg-green-50 text-green-700", iconBg: "bg-green-100" },
    { icon: TrendingUp, label: "Paid Participants", value: stats.paid_participants, color: "bg-purple-50 text-purple-700", iconBg: "bg-purple-100" },
    { icon: Award, label: "Certificates Issued", value: stats.total_certificates, color: "bg-orange-50 text-orange-700", iconBg: "bg-orange-100" },
    { icon: DollarSign, label: "Total Revenue", value: `₹${stats.total_revenue?.toLocaleString('en-IN') || 0}`, color: "bg-cyan-50 text-cyan-700", iconBg: "bg-cyan-100" },
  ] : [];

  const QUICK_ACTIONS = [
    { label: "Create Training", desc: "Add a new AI training poster", path: "/admin/trainings", color: "border-blue-200 hover:border-blue-400" },
    { label: "View Participants", desc: "Manage all registrations", path: "/admin/participants", color: "border-green-200 hover:border-green-400" },
    { label: "Issue Certificates", desc: "Generate & send certificates", path: "/admin/certificates", color: "border-orange-200 hover:border-orange-400" },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-outfit font-bold text-2xl text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm font-dm-sans mt-1">Welcome back! Here's your training overview.</p>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 p-5 animate-pulse h-28" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {STAT_CARDS.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="bg-white border border-gray-200 p-5 hover:shadow-sm transition-shadow"
            >
              <div className={`w-10 h-10 ${card.iconBg} flex items-center justify-center mb-3`}>
                <card.icon size={18} className={card.color.split(' ')[1]} />
              </div>
              <div className={`font-outfit font-bold text-2xl ${card.color.split(' ')[1]}`}>{card.value}</div>
              <div className="text-gray-500 text-xs font-dm-sans mt-1">{card.label}</div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="font-outfit font-bold text-lg text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {QUICK_ACTIONS.map((action, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => navigate(action.path)}
              className={`bg-white border ${action.color} p-5 text-left transition-all duration-200 hover:shadow-sm group`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-outfit font-bold text-gray-900 mb-1">{action.label}</h3>
                  <p className="text-gray-500 text-sm font-dm-sans">{action.desc}</p>
                </div>
                <ArrowRight size={18} className="text-gray-400 group-hover:text-gray-700 transition-colors" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 p-5">
        <h3 className="font-outfit font-bold text-blue-900 mb-2">Getting Started</h3>
        <ul className="space-y-1.5 text-sm text-blue-700 font-dm-sans">
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            Create training posters in the Trainings section
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            Share the training link with potential participants
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            Issue certificates after training completion
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center flex-shrink-0">4</span>
            Send certificates to participants via email
          </li>
        </ul>
      </div>
    </div>
  );
}
